import { createStyles, Group, Stack } from '@mantine/core';

import { CatalogRule, Filter } from '@repo/api-codegen';
import { Banner, Icon, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useCatalogList } from '../../../api/hooks/resourceCatalog';
import { FilterBlockController } from '../../../features/FilterBlock';
import { getCountDisplay } from '../../../pages/PoliciesPage/utils';
import CatalogPreviewModal from '../../CatalogPreviewModal/CatalogPreviewModal';
import { FilterOption } from '../../Filter';
import { openModal } from '../../ModalManager';

interface GovernanceFiltersProps {
	emptyStateMessage?: string;
	includedFilters: CatalogRule;
	excludedFilters: CatalogRule;
	onIncludedFiltersChange: (included: CatalogRule) => Promise<void>;
	onExcludedFiltersChange: (excluded: CatalogRule) => Promise<void>;
	previewFilter: Filter;
	filterOptions: (FilterOption | 'divider')[];
	disableTopLevelOperatorToggle?: boolean;
}

const useStyles = createStyles((theme) => ({
	resourceCountFooter: {
		padding: theme.spacing.xs,
		borderRadius: theme.radius.sm,
		backgroundColor: theme.other.getColor('surface/info/default'),
	},
	viewResources: {
		color: theme.other.getColor('text/info/default'),
		cursor: 'pointer',
		textDecoration: 'underline',
	},
}));

export const GovernanceFilters = observer(
	({
		emptyStateMessage,
		includedFilters,
		excludedFilters,
		onIncludedFiltersChange,
		onExcludedFiltersChange,
		previewFilter,
		filterOptions,
		disableTopLevelOperatorToggle,
	}: GovernanceFiltersProps) => {
		const { classes } = useStyles();

		const { data: resources, isLoading: resourcesLoading } = useCatalogList({
			page: 1,
			filters: {
				filter: previewFilter,
				grab_all_entity_types: true,
			},
		});

		const handleClick = useCallback(() => {
			openModal({
				modalId: 'resource-selection',
				variant: 'default',
				title: 'Resources',
				children: <CatalogPreviewModal filter={previewFilter} />,
			});
		}, [previewFilter]);

		return (
			<Stack spacing="md">
				<FilterBlockController
					initialRule={includedFilters}
					onRuleChange={onIncludedFiltersChange}
					filterOptions={filterOptions}
					displayPreview={false}
					ruleName="include"
					disableTopLevelOperatorToggle={disableTopLevelOperatorToggle}
					collapsible
				/>
				<FilterBlockController
					initialRule={excludedFilters}
					onRuleChange={onExcludedFiltersChange}
					filterOptions={filterOptions}
					displayPreview={false}
					ruleName="exclude"
					disableTopLevelOperatorToggle={disableTopLevelOperatorToggle}
					collapsible
				/>
				{includedFilters.operands.length > 0 ||
				excludedFilters.operands.length > 0 ? (
					<>
						{!resourcesLoading && (
							<Group spacing="xs" className={classes.resourceCountFooter}>
								<Icon name="infoCircleFilled" color="icon/info/default" />
								<Text size="sm" color="text/info/default">
									{getCountDisplay(resources?.count)} resources matching rules{' '}
									<span className={classes.viewResources} onClick={handleClick}>
										View resources
									</span>
								</Text>
							</Group>
						)}
					</>
				) : (
					<Banner tone="info" message={emptyStateMessage} />
				)}
			</Stack>
		);
	}
);
